import type { ComponentType } from 'react';
import { Columns2, Grid2x2, Rows2, Square } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/** How the compare panes are arranged. The arrangement also fixes how many
 *  panes there are: two side by side or stacked in a column, three or four in
 *  a grid.
 *  Overlaying them is a separate toggle, not an arrangement — see CompareView. */
export type CompareLayout = 'horizontal' | 'vertical' | 'grid';

/** What the viewer is showing: one slide, or two in some arrangement. */
export type Arrangement = CompareLayout | 'single';

/** The choices offered. Overlaying is deliberately absent: it is not another
 *  arrangement so much as stacking whichever arrangement you chose, and it has
 *  its own toggle beside the lock. */
const LAYOUTS: { kind: Arrangement; label: string; Icon: ComponentType<{ className?: string }> }[] =
  [
    { kind: 'single', label: 'Single slide', Icon: Square },
    { kind: 'horizontal', label: 'Horizontal', Icon: Columns2 },
    { kind: 'vertical', label: 'Vertical', Icon: Rows2 },
    { kind: 'grid', label: 'Grid (2×2)', Icon: Grid2x2 },
  ];

/** The pane arrangement, as an attached vertical dropdown — the same pattern the
 *  annotation shapes use, so every choice is visible at once instead of having to
 *  be cycled through.
 *
 *  Shared by the single-slide header and the compare toolbar so the control is in
 *  the same place and behaves the same way either side of entering compare:
 *  picking a split enters compare, picking `single` leaves it. */
export function LayoutControl({
  value,
  onSelect,
  offer,
  label = 'Compare',
}: {
  /** Where you are now: the icon on the button and the row marked current. */
  value: Arrangement;
  onSelect: (v: Arrangement) => void;
  /** Which arrangements this study can actually show — the grid needs three
   *  series. Defaults to all of them. */
  offer?: (v: Arrangement) => boolean;
  label?: string;
}) {
  const layouts = LAYOUTS.filter((l) => l.kind === value || !offer || offer(l.kind));
  const Current = (LAYOUTS.find((l) => l.kind === value) ?? LAYOUTS[0]).Icon;
  return (
    <div className="group relative">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onSelect(value)}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground',
              'group-hover:rounded-b-none group-hover:border group-hover:border-b-0 group-hover:bg-popover group-hover:text-foreground group-hover:shadow-md',
            )}
          >
            <Current className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        {/* Above: the dropdown opens below, and a tooltip hanging under the bar
            sits in the way of sliding along to the next button. */}
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
      <div className="absolute left-0 top-full z-30 hidden w-8 flex-col overflow-hidden rounded-b-md border border-t-0 bg-popover shadow-md group-hover:flex">
        {layouts.map((l) => (
          <Tooltip key={l.kind}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onSelect(l.kind)}
                className={cn(
                  'inline-flex h-8 w-full items-center justify-center transition-colors',
                  l.kind === value
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                )}
              >
                <l.Icon className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="left">{l.label}</TooltipContent>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}
